import { ScrollDepthPageName } from '@services/scroll-depth-service/scroll-depth.types';

export interface RegimenRehaulV1Variant {
  back_nudge_dr_call?: boolean;
  dr_banner_change?: boolean;
  remove_variant?: boolean;
}

export interface RegimenRehaulV1Experiment {
  key?: string;
  variant: RegimenRehaulV1Variant;
}

export interface RegimenRehaulV1Result {
  nudgeDoctorCallOnBack?: boolean;
  drBannerChange?: boolean;
  removeVariants?: boolean;
}

export interface RegimenPreparationTimerVariant {
  activationDelayInSeconds: number;
}

export interface RestrictedProductForPregnancyAndTeenagerExperiment extends Record<string, unknown> {
  variant: {
    pregnancy: boolean;
    teenager: boolean;
    approval: boolean;
    PREGNANT: string[];
    NEW_MOM: string[];
    PLANNING_FOR_PREGNANCY: string[];
    teenager_safety_profile: string[];
  };
  enable: boolean;
}

export interface ReviewOrderPageExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    shouldShowShiprocket: boolean,
  };
}

export interface RegimenReorderExperiment extends Record<string, unknown> {
  variant: {
    bannerVisibilityPhases: { startDay: number; endDay: number; hideIfOrderPlaced: boolean }[];
    minFollowupCount: number;
    minDiscountOnOrder: number;
    minRegimenProducts: number;
    maxRegimenProducts: number;
    priceSlab: { productCount: number; prices: Record<string, number> }[];
    showDoctorCharges?: boolean;
  };
  enable: boolean;
}

export interface ReportRootCauseExperiment extends Record<string, unknown> {
  variant: {
    showArrowUI: boolean;
  }
  enable: boolean;
}

export interface RegimenPricingExperiment extends Record<string,unknown> {
  enabled: boolean;
  variant;
}

export interface BottomNavConfigExperiment {
  enable: boolean;
  variant: {
    acq: string[];
    ret: string[];
  };
}

export interface ShopDynamicBannerItem {
  imageUrl: string;
  navigationUrl?: string;
}

export interface ShopDynamicBannerHeader {
  en?: string;
  hi?: string;
  te?: string;
  ml?: string;
  mr?: string;
  ta?: string;
  kn?: string;
  bn?: string;
}

export interface ShopDynamicBannerExperiment extends Record<string, unknown> {
  variant: {
    banners: ShopDynamicBannerItem[];
    header?: ShopDynamicBannerHeader;
    backgroundColor?: string;
  };
  enable: boolean;
}

export interface ChatSkipLastQuestionsExperiment extends Record<string, unknown> {
  variant: {
    questionIdsToSkipAt: string[];
    concernClasses?: string[];
  };
  enable: boolean;
}

export interface RecentlyViewedExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    SHOP: boolean;
    HOME: boolean;
  };
}

export interface RegimenProductImageCarouselExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    FACE: boolean;
    HAIR: boolean;
  };
}

export enum RecommendedProductsSurface {
  Home = 'home',
  Shop = 'shop',
}

export interface RecommendedProductsExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    'home-start-day': number;
    'home-end-day': number;
    'shop-start-day': number;
    'shop-end-day': number;
  };
}

export interface RetentionHomeRevampExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    compactBannerVisibleAfterDays: number;
    order_delivery_banner_partial_videos?: boolean;
    order_delivery_banner_start_day?: number;
    order_delivery_banner_end_day?: number;
  };
}

export interface FollowUpChatDefaultFirstAnswerExperiment extends Record<string, unknown> {
  enable: boolean;
  variant?: Record<string, unknown>;
}

export interface PrepaidDiscountVariant {
  couponCode: string;
  shipRocket?: boolean;
}

export interface PrepaidDiscountExperiment extends Record<string, unknown> {
  variant: PrepaidDiscountVariant;
  enable: boolean;
}

export interface FupQuestionsNewUiExperiment extends Record<string, unknown> {
  enable: boolean;
  variant?: {
    skipQuestion?: string[];
  };
};

export interface RegimenVariantsScrollViewCollapseExperiment {
  hideAfterTopScroll: boolean;
}

/** One female age band: applies when the user's age is <= maxAge (bands are evaluated in order). */
export interface QuizStepBand {
  maxAge: number;
  count: number;
}

/** Fixed quiz step totals per gender for a class (keys mirror ApiClientConstant.User.Gender values). */
export interface QuizStepCounts {
  Male: number;
  Female: QuizStepBand[];
}

/** Fibonacci bot-reply poll seed (ms), same t1/t2 shape as chatV2's chat_response_time variant. */
export interface QuizBotReplyDelay {
  t1: number;
  t2: number;
}

export interface ConsultationQuizExperiment extends Record<string, unknown> {
  enable: boolean;
  variant: {
    classes?: string[];
    /** Per-class fixed question counts for the quiz progress header (retunable without a release). */
    stepCounts?: Record<string, QuizStepCounts>;
    /** Overrides the quiz bot-reply poll seed; omit to keep the default 1000/1000 (2s first poll). */
    botReplyDelay?: QuizBotReplyDelay;
  };
}

/** variant maps currentLanguageCode -> headerLanguageCode. */
export interface HomeLanguageBannerExperiment {
  enable: boolean;
  variant: Record<string, string>;
}

// scroll_depth_trigger: gates the scroll_depth analytics event.
// variant.pageNames missing/empty = fire on all pages; non-empty = only listed pageNames.
export interface ScrollDepthTriggerExperiment extends Record<string, unknown> {
  variant?: {
    pageNames?: ScrollDepthPageName[];
  };
}
